import React, { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { MessagesSquare, Search, RefreshCw } from 'lucide-react';
import { threadsApi, type RecentThreadItem } from '../api/threads';
import {
  A2UISurfaceRenderer,
  ChatMessageBubble,
  type A2UISurface,
  type ChatMessage,
} from '../components/ChatComponents';
import { formatKst, formatKstDate, formatRelativeKo } from '../utils/date';

/** Both the list and the open conversation poll at this interval. */
const REFRESH_MS = 10_000;
const PAGE_SIZE = 50;
/** Newest N messages of the open thread (active + archived). */
const MESSAGE_LIMIT = 200;

type SourceFilter = 'all' | 'real' | 'playground';

const displayName = (t: RecentThreadItem): string => t.user_name?.trim() || '이름 없음';

/** UUIDv7 prefixes are timestamps and collide between clones made together; the tail does not. */
const shortId = (id: string): string => `…${id.slice(-8)}`;

/** Re-render every 30s so "3분 전" labels stay current between polls. */
const useNow = (): Date => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
};

const ChatHistory: React.FC = () => {
  const now = useNow();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<SourceFilter>('all');
  // Kept as a whole so the header survives the row scrolling out of the refetched list.
  const [selected, setSelected] = useState<RecentThreadItem | null>(null);
  /** An older thread folded into the selected row; null means the row's own (newest) thread. */
  const [olderThreadId, setOlderThreadId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const threadsQuery = useInfiniteQuery({
    queryKey: ['chatHistoryThreads', search, source],
    queryFn: ({ pageParam }) =>
      threadsApi.listRecent({
        search: search || undefined,
        source: source === 'all' ? undefined : source,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.pagination.has_more ? last.pagination.offset + last.pagination.limit : undefined),
    refetchInterval: REFRESH_MS,
    placeholderData: keepPreviousData,
  });

  // Pages are refetched independently, so a thread that just got a message can appear in two pages for a moment.
  const threads = useMemo(() => {
    const seen = new Set<string>();
    return (threadsQuery.data?.pages ?? [])
      .flatMap((p) => p.threads)
      .filter((t) => (seen.has(t.thread_id) ? false : (seen.add(t.thread_id), true)));
  }, [threadsQuery.data]);
  const total = threadsQuery.data?.pages[0]?.pagination.total ?? 0;

  // The list keeps the newest of a user's same-title threads; the header opens the others.
  const sameTitleQuery = useQuery({
    queryKey: ['chatHistorySameTitle', selected?.user_id, selected?.title],
    queryFn: () => threadsApi.listByUser(selected!.user_id, { search: selected!.title!, limit: 100 }),
    enabled: !!selected?.title && selected.same_title_threads > 1,
  });
  // search is a substring match; keep the exact title only.
  const sameTitleThreads = (sameTitleQuery.data?.threads ?? [])
    .filter((t) => t.title === selected?.title && t.last_message_at)
    .sort((a, b) => (b.last_message_at ?? '').localeCompare(a.last_message_at ?? ''));
  const threadId = olderThreadId ?? selected?.thread_id ?? null;

  const selectThread = (t: RecentThreadItem) => {
    setSelected(t);
    setOlderThreadId(null);
  };

  const messagesQuery = useQuery({
    queryKey: ['chatHistoryMessages', threadId],
    queryFn: () => threadsApi.getMessages(threadId!, { order: 'desc', source: 'all', limit: MESSAGE_LIMIT }),
    enabled: !!threadId,
    refetchInterval: REFRESH_MS,
  });

  const messages: ChatMessage[] = useMemo(
    () =>
      (messagesQuery.data?.messages ?? [])
        .filter((m) => ['human', 'user', 'ai', 'assistant'].includes(m.role) && ((m.content || '').trim() || m.tool_ui))
        .map((m) => ({
          id: m.id,
          role: m.role === 'human' || m.role === 'user' ? ('user' as const) : ('assistant' as const),
          content: (m.content || '').trim(),
          timestamp: new Date(m.timestamp),
          source: m.source,
          ...(m.tool_ui ? { toolUi: m.tool_ui as A2UISurface } : {}),
        }))
        .reverse(),
    [messagesQuery.data],
  );
  const newestMessageId = messages[messages.length - 1]?.id;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [newestMessageId]);

  return (
    <div className="flex h-full gap-4 min-h-0">
      {/* Thread list */}
      <section className="w-80 shrink-0 flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden">
        <header className="p-4 border-b border-slate-200 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <MessagesSquare size={18} /> 채팅 내역
            </h2>
            <span className="text-xs text-slate-400 flex items-center gap-1" title="10초마다 갱신">
              <RefreshCw size={12} className={threadsQuery.isFetching ? 'animate-spin' : ''} />
              대화방 {total.toLocaleString()}개
            </span>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="닉네임·user id·thread id 검색"
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-1 text-xs">
            {(
              [
                ['all', '전체'],
                ['real', '실사용자'],
                ['playground', 'Playground'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                onClick={() => setSource(value)}
                className={`px-2.5 py-1 rounded-md ${
                  source === value ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </header>

        <ul className="flex-1 overflow-y-auto divide-y divide-slate-100" data-testid="chat-history-threads">
          {threadsQuery.isError && <li className="p-4 text-sm text-red-600">목록을 불러오지 못했습니다</li>}
          {!threadsQuery.isLoading && threads.length === 0 && (
            <li className="p-4 text-sm text-slate-400">대화가 없습니다</li>
          )}
          {threads.map((t) => (
            <li
              key={t.thread_id}
              data-thread-id={t.thread_id}
              data-user-id={t.user_id}
              data-last-message-at={t.last_message_at}
            >
              <button
                onClick={() => selectThread(t)}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${
                  t.thread_id === selected?.thread_id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-800 truncate">{displayName(t)}</span>
                  <span className="flex gap-1 shrink-0">
                    {t.same_title_threads > 1 && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500"
                        title="이 계정의 같은 제목 대화방 중 가장 최근 것만 보여 줍니다"
                        data-testid="same-title-threads"
                      >
                        같은 제목 {t.same_title_threads}개
                      </span>
                    )}
                    {t.is_playground && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Playground</span>
                    )}
                  </span>
                </div>
                <div className="text-xs text-slate-600 truncate mt-0.5">{t.title || '제목 없음'}</div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-2 text-xs mt-1">
                  <dt className="text-slate-400">가입</dt>
                  <dd className="text-slate-500" data-testid="created-at">
                    {formatKstDate(t.user_created_at)}
                  </dd>
                  <dt className="text-slate-400">최근 채팅</dt>
                  <dd className="text-slate-600">
                    {formatKst(t.last_message_at)} · {formatRelativeKo(t.last_message_at, now)}
                  </dd>
                  <dt className="text-slate-400">ID</dt>
                  <dd
                    className="text-slate-400 font-mono text-[11px]"
                    title={`user ${t.user_id}\nthread ${t.thread_id}`}
                  >
                    user {shortId(t.user_id)} · thread {shortId(t.thread_id)}
                  </dd>
                </dl>
              </button>
            </li>
          ))}
          {threadsQuery.hasNextPage && (
            <li className="p-3">
              <button
                onClick={() => threadsQuery.fetchNextPage()}
                disabled={threadsQuery.isFetchingNextPage}
                className="w-full text-sm py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600"
              >
                {threadsQuery.isFetchingNextPage ? '불러오는 중…' : '더 보기'}
              </button>
            </li>
          )}
        </ul>
      </section>

      {/* Conversation */}
      <section className="flex-1 min-w-0 flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
            왼쪽에서 대화방을 고르면 대화가 보입니다
          </div>
        ) : (
          <>
            <header
              className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3"
              data-testid="chat-history-header"
            >
              <div className="min-w-0 flex-1 basis-72">
                <div className="font-semibold text-slate-800 truncate">
                  {displayName(selected)} · {selected.title || '제목 없음'}
                </div>
                <div className="text-xs text-slate-400 truncate">가입 {formatKstDate(selected.user_created_at)}</div>
                <div className="text-xs text-slate-400 truncate font-mono">
                  user {selected.user_id} · thread {threadId}
                </div>
              </div>
              {sameTitleThreads.length > 1 && (
                <select
                  value={threadId ?? ''}
                  onChange={(e) => setOlderThreadId(e.target.value === selected.thread_id ? null : e.target.value)}
                  className="text-sm border border-slate-200 rounded-lg px-2 py-1 max-w-xs"
                  data-testid="same-title-select"
                >
                  {sameTitleThreads.map((t) => (
                    <option key={t.thread_id} value={t.thread_id}>
                      {formatKst(t.last_message_at!)} · thread {shortId(t.thread_id)}
                    </option>
                  ))}
                </select>
              )}
            </header>
            <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-slate-50" data-testid="chat-history-messages">
              {messagesQuery.data?.pagination.has_more && (
                <div className="text-center text-xs text-slate-400">최근 {MESSAGE_LIMIT}개 메시지만 보여 줍니다</div>
              )}
              {messagesQuery.isError && <div className="text-sm text-red-600">대화를 불러오지 못했습니다</div>}
              {messagesQuery.isSuccess && messages.length === 0 && (
                <div className="text-sm text-slate-400">메시지가 없습니다</div>
              )}
              {messages.map((msg) => (
                <div key={msg.id}>
                  <ChatMessageBubble
                    msg={msg}
                    a2uiElement={
                      msg.toolUi ? <A2UISurfaceRenderer surface={msg.toolUi} isInteractive={false} /> : undefined
                    }
                  />
                  <div
                    className={`text-[11px] text-slate-400 mt-1 ${msg.role === 'user' ? 'text-right mr-14' : 'ml-14'}`}
                  >
                    {formatKst(msg.timestamp.toISOString())}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default ChatHistory;
